import { useQueries, type UseQueryOptions } from "@tanstack/react-query";
import { orderBy } from "lodash";
import { useMemo } from "react";
import { type Machine, type MachineType } from "@packsize/schemas/machines";

import { getCvpRangeMachinesQuery } from "./cvp-range";
import { getEmMachinesQuery } from "./em";
import { getFusionMachinesQuery } from "./fusion";
import { getLantechCaseSealerMachinesQuery } from "./lantech-case-sealer";
import { getPl1MachinesQuery } from "./pl1";
import { getSealProXMachinesQuery } from "./seal-pro-x";
import { getX4MachinesQuery } from "./x4";
import { getX5MachinesQuery } from "./x5";
import { getX6MachinesQuery } from "./x6";
import { getX7MachinesQuery } from "./x7";
import { getZebraMachinesQuery } from "./zebra";
import { type UseMachinesQueryOptions } from "./types";
import { useIsMachineTypeEnabled } from "./use-is-machine-type-enabled";

interface MachineQueryResult {
	data: Machine[] | undefined;
	enabled: boolean;
	isLoading: boolean;
	isFetching: boolean;
	isError: boolean;
	error: Error | null;
	refetch: () => Promise<unknown>;
	machineType: MachineType;
}

interface UseMachinesOptions extends UseMachinesQueryOptions<Machine[], Error> {
	machineTypes?: MachineType[];
}

const DEFAULT_OPTIONS: UseMachinesOptions = {};

export const useMachines = (options: UseMachinesOptions = DEFAULT_OPTIONS) => {
	const isMachineTypeEnabled = useIsMachineTypeEnabled();

	const cvpRangeQuery: UseQueryOptions<Machine[], Error> = getCvpRangeMachinesQuery<Machine[], Error>({
		...options,
		enabled:
			options.enabled !== false &&
			isMachineTypeEnabled("CVPRange") &&
			(!options.machineTypes || options.machineTypes.includes("CVPRange")),
	});

	const emQuery: UseQueryOptions<Machine[], Error> = getEmMachinesQuery<Machine[], Error>({
		...options,
		enabled: options.enabled !== false && (!options.machineTypes || options.machineTypes.includes("Em")),
	});

	const fusionQuery: UseQueryOptions<Machine[], Error> = getFusionMachinesQuery<Machine[], Error>({
		...options,
		enabled: options.enabled !== false && (!options.machineTypes || options.machineTypes.includes("Fusion")),
	});

	const lantechQuery: UseQueryOptions<Machine[], Error> = getLantechCaseSealerMachinesQuery<Machine[], Error>({
		...options,
		enabled: options.enabled !== false && (!options.machineTypes || options.machineTypes.includes("LantechCaseSealer")),
	});

	const pl1Query: UseQueryOptions<Machine[], Error> = getPl1MachinesQuery<Machine[], Error>({
		...options,
		enabled: options.enabled !== false && (!options.machineTypes || options.machineTypes.includes("PL1")),
	});

	const sealProXQuery: UseQueryOptions<Machine[], Error> = getSealProXMachinesQuery<Machine[], Error>({
		...options,
		enabled:
			options.enabled !== false &&
			isMachineTypeEnabled("SealProX") &&
			(!options.machineTypes || options.machineTypes.includes("SealProX")),
	});

	const x4Query: UseQueryOptions<Machine[], Error> = getX4MachinesQuery<Machine[], Error>({
		...options,
		enabled: options.enabled !== false && (!options.machineTypes || options.machineTypes.includes("X4")),
	});

	const x5Query: UseQueryOptions<Machine[], Error> = getX5MachinesQuery<Machine[], Error>({
		...options,
		enabled: options.enabled !== false && (!options.machineTypes || options.machineTypes.includes("X5")),
	});

	const x6Query: UseQueryOptions<Machine[], Error> = getX6MachinesQuery<Machine[], Error>({
		...options,
		enabled: options.enabled !== false && (!options.machineTypes || options.machineTypes.includes("X6")),
	});

	const x7Query: UseQueryOptions<Machine[], Error> = getX7MachinesQuery<Machine[], Error>({
		...options,
		enabled: options.enabled !== false && (!options.machineTypes || options.machineTypes.includes("X7")),
	});

	const zebraQuery: UseQueryOptions<Machine[], Error> = getZebraMachinesQuery<Machine[], Error>({
		...options,
		enabled: options.enabled !== false && (!options.machineTypes || options.machineTypes.includes("ZebraPrinter")),
	});

	// IMPORTANT: the result from useQueries is not referentially stable so we can't use that one as a dependency. To prevent unlimited rerenders we only use necessary props from the resulting queries.
	const [
		{
			data: cvpRangeQueryData,
			isLoading: cvpRangeQueryIsLoading,
			isFetching: cvpRangeQueryIsFetching,
			isError: cvpRangeQueryIsError,
			error: cvpRangeQueryError,
			refetch: cvpRangeQueryRefetch,
		},
		{
			data: emQueryData,
			isLoading: emQueryIsLoading,
			isFetching: emQueryIsFetching,
			isError: emQueryIsError,
			error: emQueryError,
			refetch: emQueryRefetch,
		},
		{
			data: fusionQueryData,
			isLoading: fusionQueryIsLoading,
			isFetching: fusionQueryIsFetching,
			isError: fusionQueryIsError,
			error: fusionQueryError,
			refetch: fusionQueryRefetch,
		},
		{
			data: lantechQueryData,
			isLoading: lantechQueryIsLoading,
			isFetching: lantechQueryIsFetching,
			isError: lantechQueryIsError,
			error: lantechQueryError,
			refetch: lantechQueryRefetch,
		},
		{
			data: pl1QueryData,
			isLoading: pl1QueryIsLoading,
			isFetching: pl1QueryIsFetching,
			isError: pl1QueryIsError,
			error: pl1QueryError,
			refetch: pl1QueryRefetch,
		},
		{
			data: sealProXQueryData,
			isLoading: sealProXQueryIsLoading,
			isFetching: sealProXQueryIsFetching,
			isError: sealProXQueryIsError,
			error: sealProXQueryError,
			refetch: sealProXQueryRefetch,
		},
		{
			data: x4QueryData,
			isLoading: x4QueryIsLoading,
			isFetching: x4QueryIsFetching,
			isError: x4QueryIsError,
			error: x4QueryError,
			refetch: x4QueryRefetch,
		},
		{
			data: x5QueryData,
			isLoading: x5QueryIsLoading,
			isFetching: x5QueryIsFetching,
			isError: x5QueryIsError,
			error: x5QueryError,
			refetch: x5QueryRefetch,
		},
		{
			data: x6QueryData,
			isLoading: x6QueryIsLoading,
			isFetching: x6QueryIsFetching,
			isError: x6QueryIsError,
			error: x6QueryError,
			refetch: x6QueryRefetch,
		},
		{
			data: x7QueryData,
			isLoading: x7QueryIsLoading,
			isFetching: x7QueryIsFetching,
			isError: x7QueryIsError,
			error: x7QueryError,
			refetch: x7QueryRefetch,
		},
		{
			data: zebraQueryData,
			isLoading: zebraQueryIsLoading,
			isFetching: zebraQueryIsFetching,
			isError: zebraQueryIsError,
			error: zebraQueryError,
			refetch: zebraQueryRefetch,
		},
	] = useQueries({
		queries: [
			cvpRangeQuery,
			emQuery,
			fusionQuery,
			lantechQuery,
			pl1Query,
			sealProXQuery,
			x4Query,
			x5Query,
			x6Query,
			x7Query,
			zebraQuery,
		],
	});

	return useMemo(() => {
		const result: MachineQueryResult[] = [
			{
				data: cvpRangeQueryData,
				enabled: cvpRangeQuery.enabled === true,
				isLoading: cvpRangeQueryIsLoading,
				isFetching: cvpRangeQueryIsFetching,
				isError: cvpRangeQueryIsError,
				error: cvpRangeQueryError,
				refetch: cvpRangeQueryRefetch,
				machineType: "CVPRange",
			},
			{
				data: emQueryData,
				enabled: emQuery.enabled === true,
				isLoading: emQueryIsLoading,
				isFetching: emQueryIsFetching,
				isError: emQueryIsError,
				error: emQueryError,
				refetch: emQueryRefetch,
				machineType: "Em",
			},
			{
				data: fusionQueryData,
				enabled: fusionQuery.enabled === true,
				isLoading: fusionQueryIsLoading,
				isFetching: fusionQueryIsFetching,
				isError: fusionQueryIsError,
				error: fusionQueryError,
				refetch: fusionQueryRefetch,
				machineType: "Fusion",
			},
			{
				data: lantechQueryData,
				enabled: lantechQuery.enabled === true,
				isLoading: lantechQueryIsLoading,
				isFetching: lantechQueryIsFetching,
				isError: lantechQueryIsError,
				error: lantechQueryError,
				refetch: lantechQueryRefetch,
				machineType: "LantechCaseSealer",
			},
			{
				data: pl1QueryData,
				enabled: pl1Query.enabled === true,
				isLoading: pl1QueryIsLoading,
				isFetching: pl1QueryIsFetching,
				isError: pl1QueryIsError,
				error: pl1QueryError,
				refetch: pl1QueryRefetch,
				machineType: "PL1",
			},
			{
				data: sealProXQueryData,
				enabled: sealProXQuery.enabled === true,
				isLoading: sealProXQueryIsLoading,
				isFetching: sealProXQueryIsFetching,
				isError: sealProXQueryIsError,
				error: sealProXQueryError,
				refetch: sealProXQueryRefetch,
				machineType: "SealProX",
			},
			{
				data: x4QueryData,
				enabled: x4Query.enabled === true,
				isLoading: x4QueryIsLoading,
				isFetching: x4QueryIsFetching,
				isError: x4QueryIsError,
				error: x4QueryError,
				refetch: x4QueryRefetch,
				machineType: "X4",
			},
			{
				data: x5QueryData,
				enabled: x5Query.enabled === true,
				isLoading: x5QueryIsLoading,
				isFetching: x5QueryIsFetching,
				isError: x5QueryIsError,
				error: x5QueryError,
				refetch: x5QueryRefetch,
				machineType: "X5",
			},
			{
				data: x6QueryData,
				enabled: x6Query.enabled === true,
				isLoading: x6QueryIsLoading,
				isFetching: x6QueryIsFetching,
				isError: x6QueryIsError,
				error: x6QueryError,
				refetch: x6QueryRefetch,
				machineType: "X6",
			},
			{
				data: x7QueryData,
				enabled: x7Query.enabled === true,
				isLoading: x7QueryIsLoading,
				isFetching: x7QueryIsFetching,
				isError: x7QueryIsError,
				error: x7QueryError,
				refetch: x7QueryRefetch,
				machineType: "X7",
			},
			{
				data: zebraQueryData,
				enabled: zebraQuery.enabled === true,
				isLoading: zebraQueryIsLoading,
				isFetching: zebraQueryIsFetching,
				isError: zebraQueryIsError,
				error: zebraQueryError,
				refetch: zebraQueryRefetch,
				machineType: "ZebraPrinter",
			},
		];

		const activeResults = result.filter(
			(q) => q.enabled && (!options.machineTypes || options.machineTypes.includes(q.machineType)),
		);

		return {
			machines: orderBy(
				activeResults
					.map((a) => a.data ?? [])
					.flat()
					.filter((a): a is Machine => a !== undefined),
				(a) => a.alias,
			),
			refresh: async () => {
				await Promise.all(activeResults.map((query) => query.refetch()));
			},
			isLoading: activeResults.some((s) => s.isLoading),
			isFetching: activeResults.some((s) => s.isFetching),
			isError: activeResults.some((q) => q.isError),
			error: activeResults.find((q) => q.error)?.error || null,
		};
	}, [
		options.machineTypes,
		cvpRangeQuery.enabled,
		emQuery.enabled,
		fusionQuery.enabled,
		lantechQuery.enabled,
		pl1Query.enabled,
		sealProXQuery.enabled,
		x4Query.enabled,
		x5Query.enabled,
		x6Query.enabled,
		x7Query.enabled,
		zebraQuery.enabled,
		cvpRangeQueryData,
		cvpRangeQueryError,
		cvpRangeQueryIsError,
		cvpRangeQueryIsLoading,
		cvpRangeQueryIsFetching,
		cvpRangeQueryRefetch,
		emQueryData,
		emQueryError,
		emQueryIsError,
		emQueryIsLoading,
		emQueryIsFetching,
		emQueryRefetch,
		fusionQueryData,
		fusionQueryError,
		fusionQueryIsError,
		fusionQueryIsLoading,
		fusionQueryIsFetching,
		fusionQueryRefetch,
		lantechQueryData,
		lantechQueryError,
		lantechQueryIsError,
		lantechQueryIsLoading,
		lantechQueryIsFetching,
		lantechQueryRefetch,
		pl1QueryData,
		pl1QueryError,
		pl1QueryIsError,
		pl1QueryIsLoading,
		pl1QueryIsFetching,
		pl1QueryRefetch,
		sealProXQueryData,
		sealProXQueryError,
		sealProXQueryIsError,
		sealProXQueryIsLoading,
		sealProXQueryIsFetching,
		sealProXQueryRefetch,
		x4QueryData,
		x4QueryError,
		x4QueryIsError,
		x4QueryIsLoading,
		x4QueryIsFetching,
		x4QueryRefetch,
		x5QueryData,
		x5QueryError,
		x5QueryIsError,
		x5QueryIsLoading,
		x5QueryIsFetching,
		x5QueryRefetch,
		x6QueryData,
		x6QueryError,
		x6QueryIsError,
		x6QueryIsLoading,
		x6QueryIsFetching,
		x6QueryRefetch,
		x7QueryData,
		x7QueryError,
		x7QueryIsError,
		x7QueryIsLoading,
		x7QueryIsFetching,
		x7QueryRefetch,
		zebraQueryData,
		zebraQueryError,
		zebraQueryIsError,
		zebraQueryIsLoading,
		zebraQueryIsFetching,
		zebraQueryRefetch,
	]);
};
